import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { FunctionComponent } from 'react';

import {
  CurrentEventQuery,
  FolkloreType,
} from '../../../../../apollo/__generated__/graphql';
import { NoCurrentMilestoneCard } from '../NoCurrentMilestoneCard';
import { useEventInformationCard } from './useEventInformationCard';

interface EventInformationCardProps {
  currentEvent: NonNullable<CurrentEventQuery['currentEvent']>;
}

export const EventInformationCard: FunctionComponent<
  EventInformationCardProps
> = ({ currentEvent }) => {
  const {
    onSubmit,
    errors,
    register,
    currentUser,
    currentMilestone,
    users,
    onReferentChange,
    referentId,
  } = useEventInformationCard(currentEvent);

  if (!currentUser) {
    return <CircularProgress />;
  }

  if (!currentMilestone) {
    return <NoCurrentMilestoneCard currentEvent={currentEvent} />;
  }
  return (
    <form onSubmit={onSubmit}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">{"Attestation sur l'honneur"}</Typography>

          <Typography variant="body2" component="p" sx={{ mb: 2 }}>
            {`Je soussigné(e), ${currentUser.firstname} ${
              currentUser.nickname ? `"${currentUser.nickname}" ` : ''
            }${currentUser.lastname}${
              currentUser.studies ? `, ${currentUser.studies}` : ''
            }${
              currentUser.studyTown ? ` à ${currentUser.studyTown}` : ''
            }, certifie sur l'honneur participer à l'événement « ${currentEvent.name} » et m'engage à respecter les règles et consignes définies par l'organisation.`}
          </Typography>

          <Typography variant="body2" component="p" sx={{ mb: 2 }}>
            {`
À ce titre, je m'engage à :
✅ Respecter les autres participant(e)s ainsi que l’équipe d’organisation, sans discrimination, harcèlement ou comportement inapproprié.
✅ Adopter une attitude responsable et respecter le lieu, les installations et le matériel mis à disposition.
✅ Respecter les consignes de sécurité ainsi que le règlement de l’événement (horaires, activités, déplacements, etc.).
✅ Agir avec prudence et bon sens en toutes circonstances, notamment lors des soirées et activités.
✅ Prévenir l’organisation en cas de problème, d’incident ou de situation nécessitant une intervention.
`}
          </Typography>

          <Typography variant="body2" component="p">
            {`En foi de quoi, je déclare avoir pris connaissance de ces engagements et les accepter. Je reconnais qu'en cas de non-respect, l’organisation pourra prendre toute mesure nécessaire, pouvant aller jusqu’à l’exclusion de l’événement.`}
          </Typography>
        </CardContent>

        <CardActions>
          <Box>
            <FormControlLabel
              control={<Checkbox {...register('hasValidatedTerms')} />}
              label="J'ai lu et accepté l'attestation sur l'honneur"
            />
            {errors.hasValidatedTerms?.message ? (
              <Typography component="p" variant="body2" color="error.main">
                {errors.hasValidatedTerms.message}
              </Typography>
            ) : null}
          </Box>
        </CardActions>
      </Card>

      {currentUser.folklore === FolkloreType.Impetrant ||
      currentUser.folklore === FolkloreType.Sympathisant ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">Référent</Typography>
            <Typography variant="body2">
              {`Les impétrants et les sympathisants doivent être référé par une personne sur place que l'on contacte en cas de problème.`}
            </Typography>
            <Autocomplete
              disablePortal
              options={users}
              getOptionLabel={(option) =>
                `${option.firstname} ${option.lastname}`
              }
              noOptionsText="Aucun utilisateur trouvé"
              sx={{ width: 300, mt: 4 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chercher un utilisateur sur le site"
                />
              )}
              onChange={(_, value) => {
                onReferentChange(value?.id ?? null);
              }}
            />
            {!referentId ? (
              <>
                <Typography sx={{ mt: 2 }} variant="body2">
                  ou
                </Typography>
                <Box>
                  <TextField
                    sx={{ mr: 2 }}
                    margin="normal"
                    id="referentFirstname"
                    label="Prénom"
                    autoComplete="referentFirstname"
                    type="text"
                    {...register('referentFirstname')}
                    helperText={
                      errors.referentFirstname?.message ? (
                        <Typography
                          component="p"
                          variant="body2"
                          color="error.main"
                        >
                          {errors.referentFirstname.message}
                        </Typography>
                      ) : null
                    }
                  />
                  <TextField
                    sx={{ mr: 2 }}
                    margin="normal"
                    id="referentLastname"
                    label="Nom"
                    autoComplete="referentLastname"
                    type="text"
                    {...register('referentLastname')}
                    helperText={
                      errors.referentLastname?.message ? (
                        <Typography
                          component="p"
                          variant="body2"
                          color="error.main"
                        >
                          {errors.referentLastname.message}
                        </Typography>
                      ) : null
                    }
                  />
                  <TextField
                    margin="normal"
                    id="referentPhone"
                    label="Téléphone"
                    autoComplete="referentPhone"
                    type="text"
                    {...register('referentPhone')}
                    helperText={
                      errors.referentPhone?.message ? (
                        <Typography
                          component="p"
                          variant="body2"
                          color="error.main"
                        >
                          {errors.referentPhone.message}
                        </Typography>
                      ) : null
                    }
                  />
                </Box>
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">Commentaire</Typography>
          <Typography variant="body2">
            {`Si vous avez un commentaire à ajouter, vous pouvez le faire ici.`}
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            id="comment"
            label="Commentaire"
            autoComplete="comment"
            type="text"
            multiline
            minRows={2}
            {...register('comment')}
            helperText={
              errors.comment?.message ? (
                <Typography component="p" variant="body2" color="error.main">
                  {errors.comment.message}
                </Typography>
              ) : null
            }
          />
        </CardContent>
      </Card>
      <Button type="submit" variant="contained">
        {"M'inscrire à l'événement"}
      </Button>
    </form>
  );
};
